<template>
    <div class="mag-textarea">
        <label>
            <span class="label">
                <slot name="label"></slot>
            </span>
            <span class="input" :class="{ disabled, tall, pre }">
                <textarea
                    ref="inputEl"
                    v-model="innerValue"
                    :class="{ code, _monospace: code }"
                    :disabled="disabled"
                    :required="required"
                    :readonly="readonly"
                    :placeholder="placeholder"
                    :pattern="pattern"
                    :autocomplete="autocomplete"
                    :spellcheck="spellcheck"
                    @keydown="onKeydown($event)"
                    @input="onInput"
                ></textarea>
            </span>
            <span class="caption">
                <slot name="caption"></slot>
            </span>
        </label>

        <MagButton
            v-if="manualSave && changed"
            primary
            class="save"
            @click="updated"
            icon="floppy-disk-back"
        >
            {{ $t("save") }}
        </MagButton>
    </div>
</template>

<script setup lang="ts">
import { nextTick, onMounted, ref, useTemplateRef, watch } from "vue";
import { debounce } from "throttle-debounce";

const textValue = defineModel<string>();

const innerValue = ref(textValue.value);

const props = defineProps<{
    required?: boolean;
    readonly?: boolean;
    disabled?: boolean;
    pattern?: string;
    placeholder?: string;
    autofocus?: boolean;
    autocomplete?: AutoFillNormalField;
    spellcheck?: boolean;
    code?: boolean;
    tall?: boolean;
    pre?: boolean;
    debounce?: boolean;
    manualSave?: boolean;
}>();

const emit = defineEmits<{
    change: [Event];
    keydown: [KeyboardEvent];
    enter: [];
}>();

const changed = ref(false);
const invalid = ref(false);
const inputEl = useTemplateRef("inputEl");

const onInput = (ev: Event) => {
    changed.value = true;
    emit("change", ev);
};

const onKeydown = (ev: KeyboardEvent) => {
    emit("keydown", ev);

    if (ev.code === "Enter") {
        emit("enter");
    }
};

const updated = () => {
    changed.value = false;
    textValue.value = innerValue.value;
};

const debouncedUpdated = debounce(1000, updated);

watch(textValue, (newValue) => {
    innerValue.value = newValue;
});

watch(innerValue, () => {
    if (!props.manualSave) {
        if (props.debounce) {
            debouncedUpdated();
        } else {
            updated();
        }
    }

    invalid.value = inputEl.value?.validity?.badInput ?? false;
});

onMounted(() => {
    nextTick(() => {
        if (props.autofocus) {
            focus();
        }
    });
});

function focus() {
    inputEl.value?.focus();
}

defineExpose({
    focus,
});
</script>

<style lang="scss" scoped>
.mag-textarea {
    > label {
        > .label {
            font-size: 0.85em;
            padding: 0 0 8px 0;
            user-select: none;

            &:empty {
                display: none;
            }
        }

        > .caption {
            display: block;
            font-size: 0.85em;
            padding: 8px 0 0 0;
            color: var(--fgTransparentWeak);

            &:empty {
                display: none;
            }
        }

        > .input {
            display: block;
            position: relative;

            > textarea {
                box-sizing: border-box;
                appearance: none;
                display: block;

                width: 100%;
                min-width: 100%;
                max-width: 100%;
                min-height: 130px;

                margin: 0;
                padding: 12px;
                font: inherit;
                font-weight: normal;
                font-size: 1em;
                box-shadow: none;

                color: var(--fg);
                background: var(--panel);

                border: solid 1px var(--accentedBg);
                border-radius: 6px;

                transition: border-color 0.1s ease-out;

                &:hover {
                    border-color: var(--inputBorderHover);
                }
            }

            &.disabled {
                opacity: 0.7;

                cursor: not-allowed;
            }

            &.tall {
                > textarea {
                    min-height: 200px;
                }
            }

            &.pre {
                > textarea {
                    white-space: pre;
                }
            }
        }
    }

    > .save {
        margin: 8px 0 0 0;
    }
}
</style>
